import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);
dayjs.updateLocale('en', {
  relativeTime: {
    ...dayjs.Ls.en.relativeTime,
    future: '%s',
    h: 'hour',
    m: 'minute',
    s: 'few seconds',
  },
});

interface ShippingSetup {
  title: string;
  groups: number[];
  timing: number[];
  allowSaturdayShipping: boolean;
  allowPOBox: boolean;
  cutoffHourPT: number;
}

const shippingCutOff = (cutoffHourPT: number) => {
  const currentHourInMinutes = dayjs().get('hour') * 60;
  const pacificHourInMinutes = dayjs(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).get('hour') * 60;
  const currentOffset = dayjs().utcOffset();
  const pacificOffset = currentOffset - (currentHourInMinutes - pacificHourInMinutes);
  const utcCutOffHour = Math.abs(pacificOffset / 60) + cutoffHourPT;
  const cutOff = dayjs().format('YYYY-MM-DDT[' + utcCutOffHour + ':00:00.000Z]');
  return dayjs(cutOff);
};

const shouldSkipDay = (thisDay: dayjs.Dayjs, allowSaturdayShipping: boolean) => {
  const holidayAdjustment = isHoliday(thisDay);
  const saturdayAdjustment = !allowSaturdayShipping && thisDay.format('dddd') === 'Saturday';
  const sundayAdjustment = thisDay.format('dddd') === 'Sunday';
  return holidayAdjustment || saturdayAdjustment || sundayAdjustment;
};

const skipDays = (thisDay: dayjs.Dayjs, allowSaturdayShipping: boolean): dayjs.Dayjs => {
  const tomorrow = thisDay.add(1, 'day');
  return shouldSkipDay(tomorrow, allowSaturdayShipping) ? skipDays(tomorrow, allowSaturdayShipping) : tomorrow;
};

const rawShippingDate = (offsetDays: number, allowSaturdayShipping: boolean, cutoffHourPT: number) => {
  const shipsToday = dayjs().isBefore(shippingCutOff(cutoffHourPT));
  const startDay = shipsToday ? dayjs() : dayjs().add(1, 'day');
  return Array.from(Array(offsetDays)).reduce((acc) => skipDays(acc, allowSaturdayShipping), startDay);
};

const shippingDate = (offsetDays: number, allowSaturdayShipping: boolean, cutoffHourPT: number, format = 'M/D/YYYY') => {
  return rawShippingDate(offsetDays, allowSaturdayShipping, cutoffHourPT).format(format);
};

const shippingDateFn = (allowSaturdayShipping: boolean, cutoffHourPT: number, format?: string) => {
  return (offsetDays: number) => {
    return shippingDate(offsetDays, allowSaturdayShipping, cutoffHourPT, format);
  };
};

const getGivenDateOfMonth = (startDate: string, dayOfWeek: number, weekNumber: number) => {
  const startOfMonth = dayjs(startDate).startOf('month');
  const firstDayOfWeek = startOfMonth.day(dayOfWeek);
  // Check if first firstDayOfWeek is in the given month
  const needAdditionalWeek = firstDayOfWeek.month() !== startOfMonth.month();
  const adjustedWeekNumber = needAdditionalWeek ? weekNumber + 1 : weekNumber;
  return firstDayOfWeek.add(adjustedWeekNumber - 1, 'week');
};

const lastDayOfMonth = (date: string, dayOfWeek: number, offset = 0): dayjs.Dayjs => {
  const lastDay = dayjs(date).endOf('month').add(offset, 'day');
  return lastDay.day() === dayOfWeek ? lastDay : lastDayOfMonth(date, dayOfWeek, offset - 1);
};

const isHoliday = (date: dayjs.Dayjs) => {
  const holidays = [
    date.startOf('year'),
    getGivenDateOfMonth(date.month(0).format(), 1, 3), // MLK, Third Monday in January
    getGivenDateOfMonth(date.month(1).format(), 1, 3), // Washingtons Birthday, Third Monday in February
    lastDayOfMonth(date.month(4).format(), 1), // Memorial Day, Last Monday in May
    date.month(6).date(4), // July 4th
    getGivenDateOfMonth(date.month(8).format(), 1, 1), // Labor Day, First Monday in September
    getGivenDateOfMonth(date.month(9).format(), 1, 2), // Columbus Day, Second Monday in October
    date.month(10).date(11), // Veteran's Day Nov 11th
    getGivenDateOfMonth(date.month(10).format(), 4, 4), // Thanksgiving, Fourth Thursday in November
    date.month(11).date(25), // Christmas
  ].map((h) => {
    return h.format('YYYY-MM-DD');
  });
  return holidays.includes(date.format('YYYY-MM-DD'));
};

const details = (name: ShippingMethod): ShippingSetup => {
  return {
    [ShippingMethod.Standard]: {
      title: 'Basic',
      groups: [0, 1],
      // note: this doesn't match the shipping name intentionally
      // so that we don't have to communicate cut off times
      timing: [4, 7],
      allowSaturdayShipping: true,
      allowPOBox: true,
      cutoffHourPT: 10,
    },
    [ShippingMethod.Priority]: {
      title: 'Priority',
      groups: [0],
      // note: this doesn't match the shipping name intentionally
      // so that we don't have to communicate cut off times
      timing: [2, 4],
      allowSaturdayShipping: true,
      allowPOBox: false,
      cutoffHourPT: 11,
    },
    [ShippingMethod.TwoDay]: {
      title: '2-Day',
      groups: [0],
      timing: [2],
      allowSaturdayShipping: false,
      allowPOBox: false,
      cutoffHourPT: 11,
    },
    [ShippingMethod.Overnight]: {
      title: 'Overnight',
      groups: [0],
      timing: [1],
      allowSaturdayShipping: false,
      allowPOBox: false,
      cutoffHourPT: 11,
    },
  }[name];
};

const message = (name: ShippingMethod): string => {
  const meta = details(name);
  return !!meta ? buildAnticipatedArrival(meta) : name;
};

const orderNowMessage = (name: ShippingMethod): string => {
  const meta = details(name);
  const method = shippingDateFn(meta.allowSaturdayShipping, meta.cutoffHourPT, 'MMM D');
  const arrival = meta.timing.map(method).join(' - ');
  return `Order soon for free delivery <span style="white-space: nowrap;">${arrival}<span>!`;
};

const buildAnticipatedArrival = (meta: ShippingSetup) => {
  const method = shippingDateFn(meta.allowSaturdayShipping, meta.cutoffHourPT);
  const arrival = meta.timing.map(method).join('-');
  const dayRange = meta.timing.join('-');
  const dayWord = dayRange === '1' ? 'day' : 'days';
  const title = `${meta.title} (${dayRange} business ${dayWord})`;
  return title + ` <span class="hl-anticipated-arrival" style="color: #825d12;display: block;font-size: 10pt;">Anticipated arrival: ${arrival}</span>`;
};

const isVisible = (name: ShippingMethod, address: string | null, group: number): boolean => {
  if (!address) {
    return false;
  }

  const meta = details(name);
  const isActiveGroup =
    (meta &&
      meta.groups.filter((g) => {
        return g === group;
      }).length > 0) ||
    !meta;
  const poBoxVisibility = !isPOBox(address) || (meta && meta.allowPOBox);
  return isActiveGroup && poBoxVisibility;
};

const isPOBox = (address: string) => {
  return address.match(/P[\.\s]*O[\.\s]+Box/gim);
};

export const isInHolidayShippingRange = (): boolean => dayjs().isBetween(`${dayjs().year()}-12-10`, dayjs(`${dayjs().year()}-12-25`));

export enum ShippingMethod {
  Standard = 'Basic (5-8 business days)',
  Priority = 'Priority (2-5 business days)',
  TwoDay = '2-Day (if ordered by 11 AM PST Monday-Friday)',
  Overnight = 'Overnight (if ordered by 11 AM PST Monday-Friday)',
}

export const shippingHelpers = {
  message,
  orderNowMessage,
  isVisible,
};
